import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaListUl, FaCheckCircle, FaExclamationCircle, FaSpinner, FaUpload, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import validator from 'validator';
import config from '../config';
import { useAuth } from './AuthProvider';

const Container = styled.div`
  display: flex;
  gap: 24px;
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 10px;
  }
`;

const OptionCard = styled(motion.div)`
  flex: 1;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  cursor: pointer;
  position: relative;
  overflow: visible;
  border: 2px solid ${props => props.selected ? '#0066ff' : '#f0f0f0'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.selected 
    ? '0 20px 40px rgba(0, 102, 255, 0.12), 0 4px 12px rgba(0, 102, 255, 0.08)' 
    : '0 4px 12px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)'};
  min-height: fit-content;
  height: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.selected
      ? '0 24px 48px rgba(0, 102, 255, 0.16), 0 8px 16px rgba(0, 102, 255, 0.1)'
      : '0 12px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)'};
    border-color: ${props => props.selected ? '#0066ff' : '#e0e0e0'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0066ff 0%, #0052cc 100%);
    transform: ${props => props.selected ? 'scaleX(1)' : 'scaleX(0)'};
    transform-origin: left;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${props => props.selected 
    ? 'linear-gradient(135deg, #0066ff 0%, #0052cc 100%)' 
    : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: ${props => props.selected
    ? '0 8px 16px rgba(0, 102, 255, 0.2)'
    : '0 4px 8px rgba(0, 0, 0, 0.05)'};
`;

const Icon = styled.div`
  color: ${props => props.selected ? '#ffffff' : '#666666'};
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  font-size: 14px;
  color: #666666;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const Badge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  background: ${props => props.selected
    ? 'rgba(0, 102, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.04)'};
  color: ${props => props.selected ? '#0066ff' : '#999999'};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
`;

const CheckMark = styled(motion.div)`
  position: absolute;
  top: 20px;
  right: 20px;
  color: #0066ff;
  font-size: 20px;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const InputSection = styled(motion.div)`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 18px;
  border: 2px solid ${props => {
    if (props.hasValue && props.isValid) return '#4caf50';
    if (props.hasValue && !props.isValid) return '#f44336';
    return '#f0f0f0';
  }};
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: ${props => {
    if (props.hasValue && props.isValid) return 'rgba(76, 175, 80, 0.03)';
    if (props.hasValue && !props.isValid) return 'rgba(244, 67, 54, 0.03)';
    return '#ffffff';
  }};
  color: #333333;
  box-sizing: border-box;

  &::placeholder {
    color: #999999;
  }

  &:focus {
    outline: none;
    border-color: ${props => {
      if (props.hasValue && props.isValid) return '#4caf50';
      if (props.hasValue && !props.isValid) return '#f44336';
      return '#0066ff';
    }};
    background: #ffffff;
    box-shadow: 0 0 0 3px ${props => {
      if (props.hasValue && props.isValid) return 'rgba(76, 175, 80, 0.1)';
      if (props.hasValue && !props.isValid) return 'rgba(244, 67, 54, 0.1)';
      return 'rgba(0, 102, 255, 0.1)';
    }};
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${props => 
    props.status === 'error' ? '#e74c3c' : 
    props.status === 'processing' || props.status === 'enriching' ? '#4a90e2' : 
    '#e1e8ed'
  };
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  background: ${props => 
    props.status === 'error' ? 'rgba(231, 76, 60, 0.05)' : 
    props.status === 'processing' || props.status === 'enriching' ? 'rgba(74, 144, 226, 0.05)' : 
    'transparent'
  };
  
  &:hover {
    border-color: ${props => props.status === 'error' ? '#e74c3c' : '#4a90e2'};
    background: ${props => props.status === 'error' ? 'rgba(231, 76, 60, 0.08)' : 'rgba(74, 144, 226, 0.05)'};
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e1e8ed;
  border-radius: 2px;
  margin-top: 12px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4a90e2, #357abd);
  border-radius: 2px;
`;

const StatusMessage = styled.div`
  font-size: 13px;
  color: ${props => props.error ? '#e74c3c' : '#657786'};
  margin-top: 8px;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadText = styled.p`
  color: #666666;
  font-size: 14px;
  margin: 8px 0 0 0;
`;

const UploadIcon = styled.div`
  color: #0066ff;
  font-size: 32px;
  margin-bottom: 8px;
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #0066ff 0%, #0052cc 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 102, 255, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 102, 255, 0.35);
  }

  &:disabled {
    background: #e0e0e0;
    color: #999999;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CampaignTypeSelector = ({ onDataSubmit}) => {
  const { token } = useAuth();
  const [selectedType, setSelectedType] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [urlStatus, setUrlStatus] = useState('empty'); // 'empty', 'typing', 'validating', 'valid', 'invalid'
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'validating', 'processing', 'enriching', 'completed', 'error'
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, message: '' });
  const [processedProspects, setProcessedProspects] = useState([]);

  const handleCardClick = (type) => {
    setSelectedType(type);
    setWebsiteUrl('');
    setUploadedFile(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file extension
      const allowedExtensions = ['.csv', '.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        setUploadStatus('error');
        setUploadProgress({
          current: 0,
          total: 0,
          message: 'Please upload a CSV or Excel file (.csv, .xlsx, .xls)'
        });
        setUploadedFile(null);
        e.target.value = ''; // Reset the input
        return;
      }
      
      setUploadedFile(file);
      setUploadStatus('validating');
      
      // Validate file on upload
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${config.apiUrl}/api/validate-csv`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.valid) {
          setUploadStatus('idle');
          setUploadProgress({
            current: 0,
            total: response.data.total_rows,
            message: `${response.data.total_rows} prospects detected`
          });
        } else {
          setUploadStatus('error');
          setUploadProgress({
            current: 0,
            total: 0,
            message: response.data.error || 'Invalid file format'
          });
          setUploadedFile(null);
        }
      } catch (error) {
        console.error('File validation error:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          request: error.request,
          config: error.config
        });
        
        const errorMessage = error.response?.data?.error || 
                           error.message || 
                           'Failed to validate file';
        
        setUploadStatus('error');
        setUploadProgress({
          current: 0,
          total: 0,
          message: errorMessage
        });
        setUploadedFile(null);
      }
    }
  };

  const handleSubmit = async () => {
    if (selectedType === 'scratch' && urlStatus === 'valid') {
      onDataSubmit({ type: 'website', url: websiteUrl });
    } else if (selectedType === 'list' && uploadedFile) {
      // Process CSV file
      setUploadStatus('processing');
      
      try {
        const formData = new FormData();
        formData.append('file', uploadedFile);
        
        // Use fetch API with proper headers instead of EventSource
        const response = await fetch(`${config.apiUrl}/api/upload-prospects`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Read the streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let done = false;
        
        while (!done) {
          const result = await reader.read();
          done = result.done;
          
          if (!done) {
            buffer += decoder.decode(result.value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop(); // Keep incomplete line in buffer
            
            for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                switch(data.status) {
                  case 'parsing':
                    setUploadStatus('processing');
                    setUploadProgress({ 
                      current: 0, 
                      total: data.total_prospects, 
                      message: data.message 
                    });
                    break;
                  case 'intent':
                    setUploadStatus('processing');
                    setUploadProgress({ ...uploadProgress, message: data.message });
                    break;
                  case 'enriching':
                    setUploadStatus('enriching');
                    setUploadProgress({ 
                      current: data.current, 
                      total: data.total, 
                      message: data.message 
                    });
                    if (data.prospect) {
                      // Map linkedin_url to linkedin for frontend compatibility
                      const mappedProspect = {
                        ...data.prospect,
                        linkedin: data.prospect.linkedin_url || data.prospect.linkedin
                      };
                      // Can optionally update processed prospects incrementally
                      setProcessedProspects(prev => [...prev, mappedProspect]);
                    }
                    break;
                  case 'complete': {
                    setUploadStatus('completed');
                    // Map linkedin_url to linkedin for frontend compatibility
                    const mappedProspects = (data.prospects || []).map(p => ({
                      ...p,
                      linkedin: p.linkedin_url || p.linkedin
                    }));
                    setProcessedProspects(mappedProspects);
                    setUploadProgress({ 
                      current: data.total, 
                      total: data.total, 
                      message: 'Upload and enrichment completed!' 
                    });
                    
                    console.log('Data gotten from frontend:', data);

                    // Pass enriched prospects to parent with website URL
                    onDataSubmit({ 
                      type: 'list', 
                      prospects: mappedProspects,
                      job_to_be_done: data.job_to_be_done,
                      websiteUrl: data.websiteUrl,  // Pass website URL from backend
                      scheduler_link: data.scheduler_link,  // Pass scheduler_link from backend
                      calendly_link: data.calendly_link  // Pass calendly_link from backend
                    });
                    break;
                  }
                  case 'error':
                    setUploadStatus('error');
                    setUploadProgress({ 
                      ...uploadProgress, 
                      message: data.message || 'An error occurred' 
                    });
                    break;
                  default:
                    break;
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
          }
        }
        
      } catch (error) {
        console.error('Upload error:', error);
        setUploadStatus('error');
        setUploadProgress({ 
          ...uploadProgress, 
          message: 'Failed to process file' 
        });
      }
    }
  };

  const isValidUrl = (string) => {
    if (!string || string.trim() === '') return false;
    
    // Normalize URL - add protocol if missing
    const urlWithProtocol = string.match(/^https?:\/\//i)
      ? string
      : `https://${string}`;
    
    // First, use validator.js with strict settings
    // This checks against IANA's official TLD list internally
    const isValidByValidator = validator.isURL(urlWithProtocol, {
      require_protocol: true,
      require_valid_protocol: true,
      protocols: ["http", "https"],
      require_host: true,
      require_tld: true,
      validate_length: true,
      allow_underscores: false,
      allow_trailing_dot: false,
      allow_protocol_relative_urls: false,
      disallow_auth: true
    });
    
    if (!isValidByValidator) return false;
    
    // Additional pattern-based validation for edge cases
    try {
      const urlObj = new URL(urlWithProtocol);
      const hostname = urlObj.hostname.toLowerCase();
      
      // Reject IP addresses (we want domain names)
      if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
        return false;
      }
      
      // Split domain into parts
      const domainParts = hostname.split('.');
      
      // Reject www-only domains (www.gr, www.com, etc.)
      if (domainParts[0] === 'www' && domainParts.length === 2) {
        return false;
      }
      
      // Remove www for further checks
      const domainWithoutWww = hostname.replace(/^www\./i, '');
      const partsWithoutWww = domainWithoutWww.split('.');
      
      // Must have at least domain.tld structure
      if (partsWithoutWww.length < 2) return false;
      
      // Get the second-level domain (the part before TLD)
      const sld = partsWithoutWww[partsWithoutWww.length - 2];
      const tld = partsWithoutWww[partsWithoutWww.length - 1];
      
      // SLD must be at least 2 characters (prevents things like "a.com")
      if (!sld || sld.length < 2) return false;
      
      // TLD must be at least 2 characters and only letters
      if (!tld || tld.length < 2 || !/^[a-z]+$/.test(tld)) return false;
      
      // SLD must be valid format (alphanumeric with hyphens, but not starting/ending with hyphen)
      if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(sld)) return false;
      
      return true;
    } catch (e) {
      return false;
    }
  };

  const isPartialUrl = (string) => {
    if (string.startsWith("http://") || string.startsWith("https://")) {
      const withoutProtocol = string.replace(/^https?:\/\//i, "");
      return withoutProtocol.length > 0 && !isValidUrl(string);
    }
    return false;
  };

  // Check URL validity on change
  React.useEffect(() => {
    if (!websiteUrl) {
      setUrlStatus('');
    } else if (isValidUrl(websiteUrl)) {
      setUrlStatus('valid');
    } else if (isPartialUrl(websiteUrl)) {
      setUrlStatus('partial');
    } else {
      setUrlStatus('invalid');
    }
  }, [websiteUrl]);


  return (
    <Container>
      <OptionCard
        selected={selectedType === 'scratch'}
        onClick={() => handleCardClick('scratch')}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <CheckMark
          visible={selectedType === 'scratch'}
          initial={{ scale: 0 }}
          animate={{ scale: selectedType === 'scratch' ? 1 : 0 }}
          transition={{ type: "spring", stiffness: 500 }}
        >
          <FaCheckCircle />
        </CheckMark>
        
        <IconWrapper selected={selectedType === 'scratch'}>
          <Icon selected={selectedType === 'scratch'}>
            <FaSearch />
          </Icon>
        </IconWrapper>
        
        <Title>Start from Scratch</Title>
        <Description>
          Let our AI find the perfect ICP and prospect for your business. 
          Simply provide your website and we&apos;ll handle the rest.
        </Description>
        <Badge selected={selectedType === 'scratch'}>
          AI-Powered Discovery
        </Badge>

        {selectedType === 'scratch' && (
          <InputSection
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Input
              type="url"
              placeholder="Enter your business website URL"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              hasValue={websiteUrl.length > 0}
              isValid={urlStatus === 'valid'}
            />
            <SubmitButton
              disabled={urlStatus !== 'valid'}
              onClick={handleSubmit}
              whileHover={urlStatus === 'valid' ? { scale: 1.02 } : {}}
              whileTap={urlStatus === 'valid' ? { scale: 0.98 } : {}}
            >
              Start Finding Prospects
            </SubmitButton>
          </InputSection>
        )}
      </OptionCard>

      <OptionCard
        selected={selectedType === 'list'}
        onClick={() => handleCardClick('list')}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <CheckMark
          visible={selectedType === 'list'}
          initial={{ scale: 0 }}
          animate={{ scale: selectedType === 'list' ? 1 : 0 }}
          transition={{ type: "spring", stiffness: 500 }}
        >
          <FaCheckCircle />
        </CheckMark>
        
        <IconWrapper selected={selectedType === 'list'}>
          <Icon selected={selectedType === 'list'}>
            <FaListUl />
          </Icon>
        </IconWrapper>
        
        <Title>Upload Your List</Title>
        <Description>
          Already have a list of prospects? Upload your CSV file 
          and we&apos;ll start reaching out to them immediately.
        </Description>
        <Badge selected={selectedType === 'list'}>
          Quick Import
        </Badge>

        {selectedType === 'list' && (
          <InputSection
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <FileUploadArea status={uploadStatus}>
              <FileInput
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                id="file-upload"
                disabled={uploadStatus === 'processing' || uploadStatus === 'enriching'}
              />
              <label htmlFor="file-upload" style={{ 
                cursor: uploadStatus === 'processing' || uploadStatus === 'enriching' ? 'not-allowed' : 'pointer' 
              }}>
                <UploadIcon>
                  {uploadStatus === 'processing' || uploadStatus === 'enriching' ? 
                    <FaSpinner className="fa-spin" /> : 
                    uploadStatus === 'completed' ? <FaCheckCircle /> : 
                    uploadStatus === 'error' ? <FaExclamationCircle /> :
                    <FaUpload />
                  }
                </UploadIcon>
                <UploadText>
                  {uploadStatus === 'processing' ? 'Processing file...' :
                   uploadStatus === 'enriching' ? 'Enriching prospects...' :
                   uploadStatus === 'completed' ? `Processed ${processedProspects.length} prospects` :
                   uploadedFile ? `Selected: ${uploadedFile.name}` : 
                   'Click to upload CSV or Excel file'}
                </UploadText>
              </label>
              {uploadProgress.message && (
                <StatusMessage error={uploadStatus === 'error'}>
                  {uploadProgress.message}
                </StatusMessage>
              )}
              {uploadProgress.total > 0 && (uploadStatus === 'processing' || uploadStatus === 'enriching') && (
                <ProgressBar>
                  <ProgressFill
                    initial={{ width: '0%' }}
                    animate={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </ProgressBar>
              )}
            </FileUploadArea>
            <SubmitButton
              disabled={!uploadedFile || uploadStatus === 'processing' || uploadStatus === 'enriching' || uploadStatus === 'error' || uploadStatus === 'validating' || uploadProgress.total === 0}
              onClick={handleSubmit}
              whileHover={uploadedFile && uploadStatus === 'idle' && uploadProgress.total > 0 ? { scale: 1.02 } : {}}
              whileTap={uploadedFile && uploadStatus === 'idle' && uploadProgress.total > 0 ? { scale: 0.98 } : {}}
            >
              {uploadStatus === 'validating' ? 'Validating file...' :
               uploadStatus === 'processing' ? 'Processing...' :
               uploadStatus === 'enriching' ? 'Enriching Prospects...' :
               uploadStatus === 'completed' ? 'Start Campaign' :
               uploadProgress.total > 0 ? `Process ${uploadProgress.total} Prospects` :
               'Process List'}
            </SubmitButton>
          </InputSection>
        )}
      </OptionCard>
    </Container>
  );
};

export default CampaignTypeSelector;
